import { useEffect, useRef, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";

/* v2.95 · Battery duty-cycle timeline (IEEE 485).
   Interactive staircase visualization: X = elapsed minutes, Y = load
   (Amps). Each step is a draggable rectangle. Users can add, resize
   (drag right edge to change duration), and drag height to change
   load. Also exposes battery meta parameters. */

const CANVAS_H = 300;
const PAD_T = 20;
const PAD_B = 40;
const PAD_L = 46;
const PAD_R = 20;

export default function BatteryTimeline({ onChange }) {
  const [steps, setSteps] = useState([
    { load_amps: 50,  duration_min: 60 },
    { load_amps: 150, duration_min: 5 },
  ]);
  const [meta, setMeta] = useState({
    design_margin_pct: 15, aging_factor: 1.25, temp_correction_factor: 1.11,
    end_voltage_v_per_cell: 1.75, cells_in_series: 60,
  });
  const [selected, setSelected] = useState(0);
  const svgRef = useRef(null);
  const dragMode = useRef(null);          // 'height' | 'width'

  useEffect(() => { onChange?.({ steps, ...meta }); }, [steps, meta, onChange]);

  const totalMin = Math.max(30, steps.reduce((a, s) => a + s.duration_min, 0));
  const maxA = Math.max(100, ...steps.map((s) => s.load_amps));
  const chartW = 700;
  const usableW = chartW - PAD_L - PAD_R;
  const usableH = CANVAS_H - PAD_T - PAD_B;
  const xFor = (min) => PAD_L + (min / totalMin) * usableW;
  const yFor = (a)   => CANVAS_H - PAD_B - (a / maxA) * usableH;

  const startAt = (i) => steps.slice(0, i).reduce((a, s) => a + s.duration_min, 0);

  const patchStep = (i, p) => {
    setSteps((prev) => prev.map((s, ix) => ix === i ? { ...s, ...p } : s));
  };
  const addStep = () => {
    setSteps([...steps, { load_amps: 30, duration_min: 30 }]);
    setSelected(steps.length);
  };
  const removeStep = (i) => {
    const next = steps.filter((_, ix) => ix !== i);
    setSteps(next); setSelected(Math.min(selected, next.length - 1));
  };

  const startDrag = (mode, i) => (e) => {
    e.preventDefault();
    dragMode.current = mode;
    setSelected(i);
  };
  const onMove = (e) => {
    if (dragMode.current === null) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    if (dragMode.current === "height") {
      const a = Math.max(1, ((CANVAS_H - PAD_B - y) / usableH) * maxA);
      patchStep(selected, { load_amps: Math.round(a) });
    } else if (dragMode.current === "width") {
      const startX = xFor(startAt(selected));
      const min = Math.max(1, Math.round(((x - startX) / usableW) * totalMin));
      patchStep(selected, { duration_min: min });
    }
  };
  const endDrag = () => { dragMode.current = null; };

  return (
    <div className="border border-white/10 bg-[#0A0C10]" data-testid="bat-timeline">
      <div className="flex flex-wrap items-center gap-2 p-2 border-b border-white/10 text-[10px] font-mono text-gray-400">
        <span>duty cycle · drag top of a bar to change load · drag right edge to change duration</span>
        <button onClick={addStep}
          className="ml-auto text-[10px] font-mono text-[#00E5FF] hover:bg-white/5 px-2 py-1 border border-white/10"
          data-testid="bat-add-step">
          <Plus size={10} className="inline" /> step
        </button>
      </div>

      <svg ref={svgRef} viewBox={`0 0 ${chartW} ${CANVAS_H}`} className="w-full block"
           onMouseMove={onMove} onMouseUp={endDrag} onMouseLeave={endDrag}>
        {/* Y grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const a = maxA * f;
          return (
            <g key={f}>
              <line x1={PAD_L} x2={chartW - PAD_R} y1={yFor(a)} y2={yFor(a)} stroke="#ffffff10" />
              <text x={PAD_L - 4} y={yFor(a) + 3} textAnchor="end"
                    fill="#8792A2" fontFamily="monospace" fontSize="9">
                {a.toFixed(0)} A
              </text>
            </g>
          );
        })}
        {/* X ticks */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const m = totalMin * f;
          return (
            <g key={f}>
              <line x1={xFor(m)} x2={xFor(m)} y1={CANVAS_H - PAD_B} y2={CANVAS_H - PAD_B + 4}
                    stroke="#8792A2" strokeWidth="0.5" />
              <text x={xFor(m)} y={CANVAS_H - PAD_B + 14} textAnchor="middle"
                    fill="#8792A2" fontFamily="monospace" fontSize="9">
                {m.toFixed(0)}′
              </text>
            </g>
          );
        })}
        <text x={chartW / 2} y={CANVAS_H - 6} textAnchor="middle"
              fill="#8792A2" fontFamily="monospace" fontSize="10">elapsed minutes</text>

        {/* Steps as rectangles */}
        {steps.map((s, i) => {
          const start = startAt(i);
          const x0 = xFor(start);
          const x1 = xFor(start + s.duration_min);
          const y  = yFor(s.load_amps);
          const active = selected === i;
          return (
            <g key={i} data-testid={`bat-step-${i}`}>
              <rect x={x0} y={y} width={x1 - x0} height={CANVAS_H - PAD_B - y}
                    fill={active ? "#00E5FF" : "#00E5FF66"}
                    stroke={active ? "#FFFFFF" : "#00E5FF"} strokeWidth={active ? 2 : 1}
                    onClick={() => setSelected(i)}
                    onMouseDown={startDrag("height", i)}
                    className="cursor-ns-resize" />
              {/* right edge drag handle */}
              <rect x={x1 - 4} y={y - 4} width={8} height={CANVAS_H - PAD_B - y + 8}
                    fill="transparent" onMouseDown={startDrag("width", i)}
                    className="cursor-ew-resize" />
              <text x={(x0 + x1) / 2} y={y - 6} textAnchor="middle"
                    fill="#FFFFFF" fontFamily="monospace" fontSize="9">
                {s.load_amps}A · {s.duration_min}′
              </text>
            </g>
          );
        })}
      </svg>

      {steps[selected] && (
        <div className="flex flex-wrap items-center gap-2 p-2 border-t border-white/10 bg-[#12151C]"
             data-testid="bat-step-editor">
          <span className="text-[10px] font-mono text-gray-500">step {selected + 1} · load</span>
          <input type="number" value={steps[selected].load_amps}
            onChange={(e) => patchStep(selected, { load_amps: Number(e.target.value) })}
            className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono"
            data-testid="bat-step-amps" />
          <span className="text-[10px] font-mono text-gray-500">A · duration</span>
          <input type="number" value={steps[selected].duration_min}
            onChange={(e) => patchStep(selected, { duration_min: Number(e.target.value) })}
            className="w-20 bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono"
            data-testid="bat-step-min" />
          <span className="text-[10px] font-mono text-gray-500">min</span>
          <button onClick={() => removeStep(selected)}
            className="ml-auto text-[10px] text-[#FF3B30] hover:underline inline-flex items-center gap-1"
            data-testid="bat-step-delete">
            <Trash size={10} /> remove
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-1 p-2 border-t border-white/10 text-[10px] font-mono">
        {[
          ["design_margin_pct",       "Margin %",       1],
          ["aging_factor",            "Aging",          0.01],
          ["temp_correction_factor",  "Temp corr",      0.01],
          ["cells_in_series",         "Cells",          1],
          ["end_voltage_v_per_cell",  "End V/cell",     0.01],
        ].map(([key, label, step]) => (
          <label key={key} className="flex flex-col text-gray-500">
            <span>{label}</span>
            <input type="number" step={step} value={meta[key]}
              onChange={(e) => setMeta({ ...meta, [key]: Number(e.target.value) })}
              className="bg-black/40 border border-white/10 px-1 py-0.5 text-[11px] text-gray-200"
              data-testid={`bat-meta-${key}`} />
          </label>
        ))}
      </div>
    </div>
  );
}
